import React, { useState } from 'react';
import ItemThumbnail from '../video/ItemThumbnail';
import AddToPlaylistButton from '../../app/_components/playlist/AddToPlaylistButton';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { P, H2, A } from '@osmosis/tailwind-ui/components/Text';
import AddToPlaylistIcon from '../icons/AddToPlaylistIcon';
import BreadcrumbsDropdownV2 from '../BreadcrumbsV2/BreadcrumbsDropdownV2';

import { PlaylistSearchHitType } from '../../types/search';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import { formatSecondsToDuration } from '@osmosis/shared/video';

import { track } from '../../lib/util/tracking';
import { useSearchContext } from '../../context/SearchProvider';
import { TBRLabel } from '../TBRLabel';
import LockIcon from './LockIcon';
import { useUserContext } from '../../context/UserContext';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text0, text3 } from '@osmosis/tailwind-ui/styles/text';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';

type Props = {
  data: PlaylistSearchHitType;
  index?: number;
  useDeviceSizeFallback?: boolean;
};

/**
 * Returns SearchHitRow component.
 */
const SearchHitRow = (props: Props) => {
  const [isHover, setIsHover] = useState(false);
  const { index } = props;
  const {
    transcript,
    thumb,
    breadcrumbsv2,
    category,
    watchPercent,
    concept,
    learnNodeID,
    videoDurationSec: duration,
    url,
    tags = [],
    subProducts,
  } = props.data;
  const { sm, lg, md, xl, getClassValue } = useContentSize(
    props.useDeviceSizeFallback,
  );
  const { user } = useUserContext();
  const isLockedFlag = user?.hasLockedContentFlag;

  let lockedSubProduct;
  if (subProducts && subProducts.length === 1) {
    if (subProducts[0].state === 'locked') {
      lockedSubProduct = subProducts[0].groupName
        .replace(/ /g, '_')
        .toLowerCase();
    }
  }

  const toBeRetiredTag = tags?.find(
    ({ tag }) => tag.toLowerCase() === 'to be retired',
  );

  const mockTags = [{ tag: 'to be retired', tagID: 1 }];

  const playIcon = category === 'video';

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const { search, searchResults } = useSearchContext();

  const handleClickConcept = () => {
    const index = searchResults?.findIndex((h) => h.concept === concept);

    track(
      'search',
      {
        action: 'click-result',
        query: search,
        concept,
        result_number: index,
      },
      ['keen'],
    );
  };

  const linkClassName = 'text-xs';

  const thumbnailClassName = getClassValue({
    defaultClasses: 'min-w-32 max-w-40',
    breakpoints: { md: 'min-w-32 max-w-40', base: 'min-w-28 max-w-32' },
  });

  return (
    <Box
      className="relative m-2 w-full max-w-full overflow-hidden rounded-md p-2 hover:bg-gray-100"
      data-testid="item-row-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Flex className="flex-wrap">
        <Flex
          className={cn(
            `relative min-w-32 max-w-40`,
            playIcon ? 'max-h-24' : 'max-h-40',
            thumbnailClassName,
          )}
          aria-hidden="true"
          data-testid="search-hit-row-button"
        >
          <A
            href={url}
            tabIndex={-1}
            onClick={handleClickConcept}
            data-testid={
              index === undefined ? undefined : `search-result-link-${index}`
            }
          >
            <ItemThumbnail
              thumbnailURL={`${
                /^http/.test(thumb) ? '' : PROD_MEDIA_SERVER
              }${thumb}`}
              title={concept}
              minHeight="87px"
              playIcon={playIcon}
              showDocIcon={!playIcon}
              iconLabel={playIcon ? 'Video' : 'High-yield notes'}
              duration={formatSecondsToDuration(+duration)}
              watchPercent={watchPercent / 100}
              dataTestId={category}
            />
          </A>
        </Flex>

        <Box className="relative flex-1 cursor-pointer pl-2">
          <Flex className="content-center">
            <span>
              <H2
                className={cn(
                  `mb-1 inline min-w-12 text-left text-sm`,
                  sm ? 'line-clamp-2 overflow-hidden' : '',
                )}
                data-testid="item-row-concept"
                title={concept}
              >
                <A
                  className={`!no-underline ${
                    isHover ? '!text-blue-600' : '!text-gray-600'
                  }`}
                  href={url}
                  onClick={handleClickConcept}
                  data-testid="item-row-concept-link"
                >
                  {concept}
                </A>
              </H2>
              {isLockedFlag && lockedSubProduct ? (
                <LockIcon variant={lockedSubProduct} />
              ) : null}
            </span>
          </Flex>

          {toBeRetiredTag ? (
            <TBRLabel
              tags={mockTags}
              className="ml-1.5 inline min-w-[120px] whitespace-nowrap rounded-lg text-xs"
            />
          ) : null}

          {transcript && (
            <Box
              className={cn('mb-2 mt-2 text-base ', sm ? 'block' : 'hidden')}
            >
              <P
                className={`!text-gray-500 !no-underline`}
                data-testid="item-row-transcript"
                dangerouslySetInnerHTML={{
                  __html: `... ${transcript[0].trim()} ...`,
                }}
              />
            </Box>
          )}

          {sm && breadcrumbsv2 && (
            <Flex>
              <Box className="block text-xs">
                <BreadcrumbsDropdownV2
                  breadcrumb={breadcrumbsv2}
                  sectionName={concept}
                  justifyDropdown={'flex-start'}
                  linkClassName={linkClassName}
                />
              </Box>
            </Flex>
          )}
        </Box>
        {!toBeRetiredTag && (
          <Box data-testid="playlist-button-container">
            {lg ? (
              <AddToPlaylistButton
                items={[{ name: concept, learnNodeID }]}
                data-testid="search-hit-add-to-playlist-button"
                isIconButton={false}
                buttonProps={{
                  className: cn(
                    `py-1 px-3  flex rounded-[100px] hover:bg-blue-500 hover:border-blue-500`,

                    isHover
                      ? `${text0} bg-blue-500 [&_svg]:fill-white`
                      : `${text3} ${bg0} [&_svg]:fill-gray-400`,
                    md ? 'm-0' : 'm-1',
                    xl ? 'm-2' : '',
                  ),
                  variant: 'outline',
                }}
              />
            ) : (
              <AddToPlaylistButton
                isIconButton={true}
                items={[{ name: concept, learnNodeID }]}
                data-testid="search-hit-add-to-playlist-button"
                iconButtonProps={{
                  color: '#929292' as any,
                  className: cn(
                    `p-2 mr-4 text-lg  border border-gray-300 ${
                      isHover ? 'bg-blue-500' : 'bg-white'
                    } hover:text-gray-100 hover:bg-blue-500`,
                    md ? 'block' : 'flex',
                  ),
                  'data-tip': true,
                  icon: (
                    <AddToPlaylistIcon
                      fill={`${isHover ? 'white' : 'gray60'}`}
                    />
                  ),
                }}
              />
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default SearchHitRow;
